import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import type { SavedView } from 'app/services/savedViewsApi';
import { savedViewsSaga } from './saga';
import type { SavedViewsState } from './types';

export const initialState: SavedViewsState = {
  savedViews: [],
  loading: false,
  saving: false,
  pendingDeleteCount: 0,
  error: null
};

const slice = createSlice({
  name: 'savedViews',
  initialState,
  reducers: {
    loadSavedViews(state) {
      state.loading = true;
      state.error = null;
    },
    loadSavedViewsSuccess(state, action: PayloadAction<SavedView[]>) {
      state.savedViews = action.payload;
      state.loading = false;
      state.error = null;
    },
    loadSavedViewsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    saveView(
      state,
      _action: PayloadAction<{
        name: string;
        description?: string;
        customViewLink: string;
      }>
    ) {
      state.saving = true;
      state.error = null;
    },
    saveViewSuccess(state, action: PayloadAction<SavedView>) {
      state.saving = false;
      state.savedViews = [action.payload, ...state.savedViews];
      state.error = null;
    },
    saveViewFailure(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    updateView(
      state,
      _action: PayloadAction<{
        id: string;
        name: string;
        description?: string;
        originalName: string;
        originalDescription?: string | null;
      }>
    ) {
      state.saving = true;
      state.error = null;
    },
    updateViewSuccess(state, action: PayloadAction<SavedView>) {
      state.saving = false;
      state.savedViews = state.savedViews.map(view =>
        view.id === action.payload.id ? action.payload : view
      );
      state.error = null;
    },
    updateViewFailure(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    deleteView(state, _action: PayloadAction<{ id: string }>) {
      state.pendingDeleteCount += 1;
      state.error = null;
    },
    deleteViewSuccess(state, action: PayloadAction<string>) {
      state.pendingDeleteCount = Math.max(0, state.pendingDeleteCount - 1);
      state.savedViews = state.savedViews.filter(
        view => view.id !== action.payload
      );
      state.error = null;
    },
    deleteViewFailure(state, action: PayloadAction<string>) {
      state.pendingDeleteCount = Math.max(0, state.pendingDeleteCount - 1);
      state.error = action.payload;
    }
  }
});

export const { actions: savedViewsActions, reducer } = slice;

export const useSavedViewsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: savedViewsSaga });
  return { actions: savedViewsActions };
};
